import { useState } from "react";
import { Menu, X, Phone, Languages } from "lucide-react";
import { LOGO_URL, PHONE_TEL, PHONE_DISPLAY } from "@/constants/site";
import { useLanguage } from "@/i18n/LanguageContext";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { t, lang, toggle } = useLanguage();

  const links = [
    { id: "services", label: t.nav.services, href: "#services" },
    { id: "process", label: t.nav.process, href: "#process" },
    { id: "work", label: t.nav.work, href: "#work" },
    { id: "why-us", label: t.nav.whyUs, href: "#why-us" },
    { id: "reviews", label: t.nav.reviews, href: "#reviews" },
    { id: "contact", label: t.nav.contact, href: "#contact" },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-40 backdrop-blur-xl bg-[#1A1412]/80 border-b border-[#D4AF37]/20" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between h-20">
        <a href="#top" className="flex items-center gap-3" data-testid="nav-logo-link">
          <img src={LOGO_URL} alt="The Build and Design logo" className="h-12 w-12 object-cover rounded-sm" />
          <div className="leading-tight">
            <span className="font-heading text-xl text-[#D4AF37] block">The Build and Design</span>
            <span className="text-[10px] tracking-[0.3em] text-[#B8A99A] uppercase">{t.brand.tagline}</span>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-8" data-testid="nav-desktop-menu">
          {links.map((l) => (
            <a
              key={l.id}
              href={l.href}
              data-testid={`nav-link-${l.id}`}
              className="text-sm tracking-widest uppercase text-[#FDFBF7]/80 hover:text-[#D4AF37] transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
          <button
            onClick={toggle}
            data-testid="language-toggle"
            className="flex items-center gap-2 border border-[#D4AF37]/60 text-[#D4AF37] px-4 py-2.5 text-sm tracking-widest uppercase hover:bg-[#D4AF37] hover:text-[#1A1412] transition-colors duration-300"
          >
            <Languages className="h-4 w-4" /> {lang === "en" ? "हिंदी" : "EN"}
          </button>
          <a
            href={PHONE_TEL}
            data-testid="nav-call-button"
            className="flex items-center gap-2 bg-[#D4AF37] text-[#1A1412] px-5 py-2.5 text-sm tracking-widest uppercase font-semibold hover:bg-[#E5C15D] transition-colors duration-300"
          >
            <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
          </a>
        </nav>

        <button
          className="lg:hidden text-[#D4AF37]"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label={t.aria.toggleMenu}
        >
          {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
        </button>
      </div>

      {open && (
        <nav className="lg:hidden bg-[#1A1412] border-t border-[#D4AF37]/20 px-6 py-6 flex flex-col gap-5" data-testid="nav-mobile-menu">
          {links.map((l) => (
            <a
              key={l.id}
              href={l.href}
              onClick={() => setOpen(false)}
              data-testid={`nav-mobile-link-${l.id}`}
              className="text-sm tracking-widest uppercase text-[#FDFBF7]/80 hover:text-[#D4AF37] transition-colors"
            >
              {l.label}
            </a>
          ))}
          <button
            onClick={toggle}
            data-testid="language-toggle-mobile"
            className="flex items-center gap-2 border border-[#D4AF37]/60 text-[#D4AF37] px-5 py-3 text-sm tracking-widest uppercase w-fit"
          >
            <Languages className="h-4 w-4" /> {lang === "en" ? "हिंदी" : "EN"}
          </button>
          <a
            href={PHONE_TEL}
            data-testid="nav-mobile-call-button"
            className="flex items-center gap-2 border border-[#D4AF37]/60 text-[#D4AF37] px-5 py-3 text-sm tracking-widest uppercase w-fit"
          >
            <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
          </a>
        </nav>
      )}
    </header>
  );
};
